import { JSX } from 'react';

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import httpInstances from '@/api/axiosInstance';
import { TileType } from '@/pages/Markets/MarketsComponents/Tiles/types';
import {
  ChartsBlockTabType,
  CommoditiesTableTabType,
  MiniTableTableTab
} from '@/pages/Markets/MarketsComponents/types';
import { detectEnvironment } from '@/utils/detectEnvironment';
import { parseMarketComponent } from '@/utils/parseMarketComponent';
import { sanitizeName } from '@/utils/sanitizeName';

export type MarketsState = {
  status: 'idle' | 'cancelled' | 'loading' | 'failed' | 'fulfilled';
  error: string | null;
  markets: Market | null;
};

type ErrorResponse = {
  message: string;
};

export type Market = {
  metadata: {
    displayName: string;
    commodity: string[];
    region: string[];
    comingSoon: boolean;
  };
  pages: MarketsPageType[];
  id: string;
};

export type MarketDto = {
  version?: 'v2';
  metadata: {
    displayName: string;
    commodity: string[];
    region: string[];
    comingSoon: boolean;
  };
  pages: DataPageType[];
  id: string;
};

export type DataPageType = {
  id: string;
  name: string;
  components: DataComponentType[];
};

export type MarketsPageType = {
  id: string;
  name: string;
  components: MarketsComponentType[];
};

type BaseMarketsComponentType = {
  name: string;
  id: string;
  metadata?: Record<string, string>;
  layout?: Record<string, string>;
};

export type MarketsTableType = BaseMarketsComponentType & {
  type: 'table';
  tabs?: CommoditiesTableTabType[];
} & CommoditiesTableTabType;

export type MarketsTilesType = BaseMarketsComponentType & {
  type: 'tiles';
  tiles: TileType[];
};

export type MarketsGraphType = BaseMarketsComponentType & {
  type: 'graph';
  tabs?: CommoditiesTableTabType[];
} & ChartsBlockTabType<unknown>;

export type MarketsGraphStaticType = BaseMarketsComponentType & {
  type: 'graph-static';
  tabs?: ChartsBlockTabType<unknown>[];
};

export type MarketsFeedType = BaseMarketsComponentType & {
  type: 'feed';
  tag?: JSX.Element;
};

export type MarketsMinitableType = BaseMarketsComponentType & {
  type: 'minitable';
  tabs?: CommoditiesTableTabType[];
} & MiniTableTableTab;

export type CSVTableTabType = BaseMarketsComponentType & {
  type: 'csvtable';
  fileName: string;
  tabs?: CommoditiesTableTabType[];
  groupName: string;
};

export type FilesType = BaseMarketsComponentType & {
  type: 'file_list';
  groupName: string;
  size?: number;
};

export type BaseTabType =
  | MarketsTableType
  | MarketsTilesType
  | MarketsGraphType
  | MarketsFeedType
  | MarketsMinitableType
  | CSVTableTabType
  | MarketsGraphStaticType
  | FilesType;

export type MarketsComponentType = {
  layout?: {
    fullWidth?: boolean;
    height?: number;
  };
  component?: BaseTabType;
  tabs?: BaseTabType[];
  id: string;
};
type DataMarketsComponentType = {
  layout?: {
    fullWidth?: boolean;
    height?: number;
  };
  metadata?: Record<string, string>;
  id: string;
};

type DataTableType = DataMarketsComponentType & {
  type: 'table';
  tabs: CommoditiesTableTabType[];
};

type DataTilesType = DataMarketsComponentType & {
  type: 'tiles';
  tiles: TileType[];
};

type DataGraphType = DataMarketsComponentType & {
  type: 'graph';
  tabs: ChartsBlockTabType<unknown>[];
};

type DataFeedType = DataMarketsComponentType & {
  type: 'feed';
};

type DataMinitableType = DataMarketsComponentType & {
  type: 'minitable';
  tabs: MiniTableTableTab[];
};

export type DataComponentType =
  | DataTableType
  | DataTilesType
  | DataGraphType
  | DataFeedType
  | DataMinitableType;

const initialState: MarketsState = {
  status: 'idle',
  error: null,
  markets: null
};
let controller: AbortController;

export const getMarkets = createAsyncThunk(
  'getMarkets',

  async ({ marketId }: { marketId: string }, { rejectWithValue }) => {
    try {
      if (controller) {
        controller.abort();
      }
      controller = new AbortController();
      const signal = controller.signal;

      const { data }: { data: MarketDto } = await httpInstances.axiosInternalInstance.get(
        `${detectEnvironment() === 'staging' ? '/staging/' : '/'}markets/${marketId}`,
        { signal }
      );

      const pages = data.pages.map(page => ({
        ...page,
        id: sanitizeName(page.name),
        components: page.components.map(component => {
          return parseMarketComponent(component, data?.version);
        })
      }));

      return {
        ...data,
        id: marketId,
        pages
      };
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        const errorResponse = error.response.data;
        return rejectWithValue({ message: errorResponse, status: error.status });
      }
      throw error;
    }
  }
);

const marketsSlice = createSlice({
  name: 'markets',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getMarkets.pending, state => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(getMarkets.fulfilled, (state, action) => {
        state.status = 'fulfilled';
        state.markets = action.payload;
      })

      .addCase(getMarkets.rejected, (state, action) => {
        if (action.error?.name === 'CanceledError') {
          state.status = 'cancelled';
        } else {
          state.status = 'failed';
        }
        if (action.payload) {
          state.error = (action.payload as ErrorResponse).message ?? 'Request failed';
        } else {
          state.error = action.error.message ?? 'Request failed';
        }
      });
  }
});

export default marketsSlice.reducer;
